import { createPortal } from 'react-dom';
import type { ProductLinkInput } from '../../../features/materials/api';
import { useTags } from '../../../features/tags';
import { brandConfig } from '../../../config/brands';
import { CreateTagModal } from '../../tags/CreateTagModal';
import tagSelectorStyles from '../../tags/TagSelector.module.css';
import { BulkActionsBar } from '../../common/BulkActionsBar';
import { ProductSelectorModal } from '../../materials/shared/ProductSelectorModal';
import type { BulkEditMode, BulkMaterialActionsApi } from './useBulkMaterialActions';
import type { TabType } from './types';

interface Props {
  bulk: BulkMaterialActionsApi;
  bulkEditMode: BulkEditMode;
  onBulkEditComplete?: () => void;
  activeTab: TabType;
  /** 一括操作バーの portal 先。未指定なら document.body にビューポート fixed */
  bulkActionsTarget?: HTMLElement | null;
}

/**
 * 一括編集モードの操作バー (削除 / タグ / フォルダ移動 / 商品関連付け) と、
 * それに付随するタグ選択ドロップダウン・タグ新規作成モーダル・商品選択モーダル。
 */
export function MaterialsBulkActions({
  bulk,
  bulkEditMode,
  onBulkEditComplete,
  activeTab,
  bulkActionsTarget,
}: Props) {
  const tagsQuery = useTags();
  const {
    bulkSelectedIds, setBulkSelectedIds,
    isTagDropdownOpen, setTagDropdownOpen,
    productSelectorMode, setProductSelectorMode,
    removableLinks, setRemovableLinks,
    tagDropdownPosition, setTagDropdownPosition,
    isCreateTagModalOpen, setCreateTagModalOpen,
    tagButtonRef, tagDropdownRef,
    handleBulkDelete,
    handleBulkMove,
    handleBulkAddTag,
    handleBulkRemoveTag,
    handleBulkProductLinks,
    openRemoveProductLinkModal,
  } = bulk;

  /** タグドロップダウンをボタン位置に合わせて開閉する */
  const toggleTagDropdown = (e: React.MouseEvent<HTMLButtonElement>, dropdownMode: 'add' | 'remove') => {
    if (!isTagDropdownOpen) {
      const rect = e.currentTarget.getBoundingClientRect();
      const dropdownWidth = 280;
      const dropdownMaxHeight = 350;
      const spacing = 8;
      const viewportPadding = 8;

      let top = rect.top;
      const bottomOverflow = top + dropdownMaxHeight - window.innerHeight + viewportPadding;

      if (bottomOverflow > 0) {
        top = Math.max(viewportPadding, top - bottomOverflow);
      }

      setTagDropdownPosition({
        top,
        left: rect.left - dropdownWidth - spacing,
      });
      setTagDropdownOpen(dropdownMode);
    } else {
      setTagDropdownOpen(isTagDropdownOpen === dropdownMode ? null : dropdownMode);
    }
  };

  return (
    <>
      {/* 一括削除ボタン（削除モード時のみ） */}
      {bulkEditMode === 'delete' && (
        <BulkActionsBar
          selectedCount={bulkSelectedIds.size}
          target={bulkActionsTarget}
          onCancel={() => {
            onBulkEditComplete?.();
            setBulkSelectedIds(new Set());
          }}
        >
          <button
            type="button"
            className="bulk-action-btn bulk-delete-btn"
            onClick={handleBulkDelete}
            disabled={bulkSelectedIds.size === 0}
          >
            一括削除
          </button>
        </BulkActionsBar>
      )}

      {/* タグ編集UI（タグモード時のみ） */}
      {bulkEditMode === 'tags' && (
        <BulkActionsBar
          selectedCount={bulkSelectedIds.size}
          target={bulkActionsTarget}
          onCancel={() => {
            onBulkEditComplete?.();
            setBulkSelectedIds(new Set());
          }}
        >
          <button
            type="button"
            className="bulk-action-btn bulk-delete-btn"
            onClick={(e) => toggleTagDropdown(e, 'remove')}
            disabled={bulkSelectedIds.size === 0}
          >
            タグを削除
          </button>
          <button
            ref={tagButtonRef}
            type="button"
            className="bulk-action-btn tag-action-btn"
            onClick={(e) => toggleTagDropdown(e, 'add')}
            disabled={bulkSelectedIds.size === 0}
          >
            タグを追加
          </button>
        </BulkActionsBar>
      )}

      {/* フォルダ移動UI（移動モード時のみ） */}
      {bulkEditMode === 'move' && (
        <BulkActionsBar
          selectedCount={bulkSelectedIds.size}
          target={bulkActionsTarget}
          onCancel={() => {
            onBulkEditComplete?.();
            setBulkSelectedIds(new Set());
          }}
        >
          <button
            type="button"
            className={`bulk-action-btn bulk-move-btn${activeTab === 'cloud' ? ' bulk-move-personal' : ''}`}
            onClick={handleBulkMove}
            disabled={bulkSelectedIds.size === 0}
          >
            {activeTab === 'temporary' ? '共有フォルダに移動' : '個人フォルダに移動'}
          </button>
        </BulkActionsBar>
      )}

      {/* 商品の関連付けUI（product-linkモード時のみ） */}
      {bulkEditMode === 'product-link' && (
        <BulkActionsBar
          selectedCount={bulkSelectedIds.size}
          target={bulkActionsTarget}
          onCancel={() => {
            onBulkEditComplete?.();
            setBulkSelectedIds(new Set());
            setProductSelectorMode(null);
          }}
        >
          <button
            type="button"
            className="bulk-action-btn bulk-delete-btn"
            onClick={openRemoveProductLinkModal}
            disabled={bulkSelectedIds.size === 0}
          >
            関連付けを解除
          </button>
          <button
            type="button"
            className="bulk-action-btn tag-action-btn"
            onClick={() => setProductSelectorMode('add')}
            disabled={bulkSelectedIds.size === 0}
          >
            {brandConfig.labels.productShort}を関連付け
          </button>
        </BulkActionsBar>
      )}

      {/* 商品選択モーダル（一括追加 / 解除）。
          解除モードは「紐づいている商品を全部外す」が既定動作なので初期状態で全選択にする */}
      {productSelectorMode !== null && (
        <ProductSelectorModal
          isOpen={productSelectorMode !== null}
          onClose={() => {
            setProductSelectorMode(null);
            setRemovableLinks([]);
          }}
          restrictToLinks={productSelectorMode === 'remove' ? removableLinks : undefined}
          initialSelectedLinks={productSelectorMode === 'remove' ? removableLinks : undefined}
          confirmLabel={productSelectorMode === 'remove' ? '関連付けを解除' : undefined}
          onSelectLinks={(selected) => {
            const links: ProductLinkInput[] = selected.map((s) => ({
              product_type: s.product_type,
              product_id: s.product_id,
            }));
            handleBulkProductLinks(links, productSelectorMode);
            setRemovableLinks([]);
          }}
        />
      )}

      {/* タグ選択ドロップダウン - Portal経由 */}
      {isTagDropdownOpen && createPortal(
        <div
          ref={tagDropdownRef}
          className={tagSelectorStyles.dropdown}
          style={{
            position: 'fixed',
            top: `${tagDropdownPosition.top}px`,
            left: `${tagDropdownPosition.left}px`,
          }}
        >
          {/* ヘッダーと閉じるボタン */}
          <div className={tagSelectorStyles.dropdownHeader}>
            <span className={tagSelectorStyles.dropdownTitle}>
              {isTagDropdownOpen === 'add' ? 'タグを追加' : 'タグを削除'}
            </span>
            <button
              type="button"
              onClick={() => setTagDropdownOpen(null)}
              className={tagSelectorStyles.dropdownCloseButton}
              aria-label="閉じる"
            >
              ✕
            </button>
          </div>

          {tagsQuery.data && tagsQuery.data.length > 0 ? (
            <div className={tagSelectorStyles.tagList}>
              {tagsQuery.data.map((tag) => (
                <button
                  key={tag.id}
                  type="button"
                  onClick={() => {
                    if (isTagDropdownOpen === 'add') {
                      handleBulkAddTag(tag);
                    } else if (isTagDropdownOpen === 'remove') {
                      handleBulkRemoveTag(tag);
                    }
                  }}
                  className={tagSelectorStyles.tagOption}
                  style={tag.color ? { borderLeftColor: tag.color } : undefined}
                >
                  {tag.name}
                </button>
              ))}
            </div>
          ) : (
            <div className={tagSelectorStyles.emptyState}>
              {tagsQuery.isLoading ? 'タグを読み込み中...' : '利用可能なタグがありません'}
            </div>
          )}

          {/* 追加モードの場合のみ新規作成ボタンを表示 */}
          {isTagDropdownOpen === 'add' && (
            <>
              <div className={tagSelectorStyles.divider} />
              <button
                type="button"
                onClick={() => {
                  // ドロップダウンは開いたままにする
                  setCreateTagModalOpen(true);
                }}
                className={tagSelectorStyles.createButton}
              >
                + 新規作成
              </button>
            </>
          )}
        </div>,
        document.body
      )}

      {/* タグ新規作成モーダル */}
      <CreateTagModal
        isOpen={isCreateTagModalOpen}
        onClose={() => {
          setCreateTagModalOpen(false);
          // ドロップダウンは開いたままにする
        }}
        onCreated={async (tagName) => {
          // 新規作成したタグを自動的に追加
          await tagsQuery.refetch();
          const newTag = tagsQuery.data?.find(t => t.name === tagName);
          if (newTag) {
            await handleBulkAddTag(newTag);
          }
        }}
        position={tagDropdownPosition}
      />
    </>
  );
}
